import { useMemo, type ReactElement } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { AnswerSource, AnswerStats } from 'src/redux/survey/survey.types';
import { feedbackListForFilter } from 'src/pages/view-survey/view-survey.helpers';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { emptyFeedbackMessage } from '../../services/source-filter.helpers';
import ResponseSourceBadge from './response-source-badge';

interface QuestionFeedbackRegionProps {
  answers: AnswerStats[];
  sourceFilter: AnswerSource;
}

function QuestionFeedbackRegion({
  answers,
  sourceFilter,
}: QuestionFeedbackRegionProps): ReactElement {
  const feedbackEntries = useMemo(() => {
    return answers.flatMap((answer) => {
      if (!answer.feedbacks) {
        return [];
      }

      const answerLabel = answer.displayName || answer.title;
      return feedbackListForFilter(answer.feedbacks, sourceFilter).map((entry) => ({
        ...entry,
        answerLabel,
      }));
    });
  }, [answers, sourceFilter]);

  return (
    <Collapsible
      defaultOpen={false}
      className="group/collapsible mt-4 border-t border-[#EDEEF2] pt-4"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 text-left">
        <span className={portalDs.headingH3}>
          Feedback (
          {feedbackEntries.length}
          )
        </span>
        <ChevronDown className="size-4 shrink-0 text-[#9CA3AF] transition-transform group-data-[state=open]/collapsible:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">
        {feedbackEntries.length > 0 ? (
          <ul className="space-y-2">
            {feedbackEntries.map((entry, index) => (
              <li
                key={`${entry.answerLabel}-${entry.createdAt}-${index}`}
                className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] px-3 py-2.5"
              >
                {entry.answerLabel ? (
                  <p className={cn(portalDs.caption, 'mb-1')}>
                    {entry.answerLabel}
                  </p>
                ) : null}
                <p className={cn(portalDs.bodySm, 'text-[#0D0F1A]')}>
                  {entry.feedback}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <ResponseSourceBadge userType={entry.userType} />
                  {entry.createdAt ? (
                    <span className={portalDs.caption}>{formatDisplayDateTime(entry.createdAt)}</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className={portalDs.bodySm}>
            {emptyFeedbackMessage(sourceFilter)}
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default QuestionFeedbackRegion;
